import React, { useEffect, useState } from 'react';
import styles from './ccf-app-header.module.scss';
import Badge from '@mui/material/Badge';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  getApplicationLocale,
  getCopilotConfig,
} from '@nice-ccf/ui-components';
import {
  AppBar,
  Toolbar,
  IconButton,
  Theme,
  Grid,
  alpha,
  Link,
  useTheme,
  Divider,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { ToastContainer } from 'react-toastify';
import CcfAgentState from '../ccf-agent-state/ccf-agent-state';
import {
  CcfAgentDashboardIcon,
  CcfBox,
  CcfCxoneLogoIcon,
  CcfHelpIcon,
  CcfNavigateToSimulatorIcon,
  CcfNotificationIcon,
  CcfReloadIcon,
  CcfTooltip,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  getPartnerAccessTabVisibilityState,
  getSimulatorTabVisibilityState,
  globalActions,
} from '../global.app.slice';
import { useDispatch, useSelector } from 'react-redux';
import CcfAppSpaceStyles from './ccf-app-header-styles';
import CcfAgentLeg from '../ccf-agent-leg/ccf-agent-leg';
import {
  getLastContacts,
  setJsonFileUploadStatus,
  useSimulatorToInsertContact,
} from '../ccf-inbox/ccf-inbox.slice';
import { authorizePartnersAccess } from '../login/mock-data/authorizePartnersAccess';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { selectLoginCredentialsState } from '../login/+state/login.slice';
import AWS from 'aws-sdk';
import { CcfAcdHeaderPopover } from '../ccf-acd-header/ccf-acd-header-popover';
import { awsS3Config } from '../../util/common';

const adminAccessEmails = [
  'Alexei.Frumkin@nice.com',
  'Andy.Marchky@nice.com',
  'Anthony.Dang@nice.com',
  'Chris.Lounsbury@nice.com',
  'fernando.lopez@nice.com',
  'Julie.Williams@nice.com',
  'Oscar.Wolf@nice.com',
  'Tom.Jones@nice.com',
  'Brett.Crutchfield@nice.com',
  'Carmit.DiAndrea@nice.com',
  'Sandeep.Giri@nice.com',
  'nikita.chaudhari@nice.com',
].map((email) => email.toLowerCase());

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    flexGrow: 1,
  },
  menuButton: {
    marginRight: theme.spacing(2),
  },
  title: {
    flexGrow: 1,
    display: 'none',
    [theme.breakpoints.up('sm')]: {
      display: 'block',
    },
  },
  search: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${alpha(theme.palette.common.white, 0.5)}`,
    marginLeft: 0,
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1),
      width: 'auto',
    },
  },
  searchIcon: {
    padding: theme.spacing(0, 1),
    color: alpha(theme.palette.common.white, 0.5),
    height: '50%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputRoot: {
    color: 'inherit',
  },
  inputInput: {
    color: 'white',
    padding: theme.spacing(0.5, 1, 0.5, 0),
    paddingLeft: `calc(1em + ${theme.spacing(3)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '120px',
      '&:focus': {
        width: '120px',
      },
    },
  },
}));

const supportedLanguages = [
  { code: 'en-US', label: 'English' },
  { code: 'pt-BR', label: 'Português' },
];

/**
 *
 * @example useIsAdminUser()
 */
const useIsAdminUser = (adminAccessEmails: string[]): boolean => {
  const { accounts } = useMsal();
  const loggedInUsername = accounts[0]?.username?.toLowerCase();
  if (!loggedInUsername) return false;

  const adminUsernames = adminAccessEmails.map((email) => email.split('@')[0]);
  const [loggedInUsernamePart, loggedInDomain] = loggedInUsername.split('@');

  return (
    adminUsernames.includes(loggedInUsernamePart) &&
    loggedInDomain.includes('nice')
  );
};

/* eslint-disable-next-line */
export interface CcfAppHeaderProps {
  headerText?: string;
  showHeaderText?: boolean;
}

const s3Config = {
  accessKeyId: awsS3Config.accessKeyId,
  secretAccessKey: awsS3Config.secretAccessKey,
  s3SupervisorBucket:
    window.location.hostname === 'd1u959wi0vug3n.cloudfront.net'
      ? 'cxone-supervisor-html-demo-app'
      : 'cxone-supervisor-html-demo-test-app',
  region: awsS3Config.region,
};

AWS.config.update({
  accessKeyId: s3Config.accessKeyId,
  secretAccessKey: s3Config.secretAccessKey,
  region: s3Config.region,
});

const s3 = new AWS.S3();

/**
 * Component displays App header on landing screen
 * @param props -CcfAppHeaderProps
 * @returns app header component for landing screen
 * @example <CcfAppHeader/>
 */
export function CcfAppHeader(props: CcfAppHeaderProps) {
  const { headerText, showHeaderText } = props;
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const [count, setCount] = useState(0);
  const [showPopOver, setShowPopOver] = useState(false);
  const [popoverFeature, setPopoverFeature] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isSimulatorTabVisible = useSelector(getSimulatorTabVisibilityState);
  const isPartnerAccessTabVisible = useSelector(
    getPartnerAccessTabVisibilityState
  );
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const loginCredentials = useSelector(selectLoginCredentialsState);
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const loginCredentialsArray = loginCredentialsFromSSO?.split(' ');
  const assignName = isAuthenticated
    ? `${
        loginCredentialsArray[0].charAt(0).toUpperCase() +
        loginCredentialsArray[0].slice(1).toLowerCase()
      } ${
        loginCredentialsArray[1].charAt(0).toUpperCase() +
        loginCredentialsArray[1].slice(1).toLowerCase()
      }`
    : loginCredentials?.firstName + ' ' + loginCredentials?.lastName;
  const { setShowPartnerAccessPage, setConversationMessage } = globalActions;
  const url = window.location.host;
  const currentUrlPathName = window.location.pathname;
  const appHeaderStyles = CcfAppSpaceStyles(theme);
  const lastContact = useSelector(getLastContacts);
  const locale = useSelector(getApplicationLocale);
  const { setLocale } = globalActions;
  const [translate] = useTranslator();
  const coPilotConfigurations = useSelector(getCopilotConfig);
  const isAuthorized = useIsAdminUser(adminAccessEmails);
  const [conversationReplyMessage, setConversationReplyMessage] =
    useState(null);

  /**
   * Marks the conversation notification as read:
   * resets badge count to 0 and removes the notification entry.
   * @example handleConversationNotificationRead()
   */
  const handleConversationNotificationRead = () => {
    setCount(0);
    setConversationReplyMessage(null);
  };

  // eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const selectedLanguage = event.target.value;
    dispatch(setLocale(selectedLanguage));
  };

  /**
   *
   * @example
   */
  function navigateToSimulator() {
    if (url === 'localhost:4200') {
      window.open(`/simulator-v2`);
    } else {
      const urlPath = 'https://' + window.location.hostname + `/simulator-v2`;
      window.open(urlPath);
    }
  }

  /**
   *
   * @example
   */
  function navigateToPartnerAccess() {
    dispatch(setShowPartnerAccessPage(true));
  }

  const fetchS3ConversationMessages = async () => {
    try {
      // ── Step 1: Fetch message from S3 ─────────────────────────────
      const data = await s3
        .getObject({
          Bucket: s3Config.s3SupervisorBucket,
          Key: `messages/supervisor-to-agent.json`,
          ResponseCacheControl: 'no-cache, no-store, must-revalidate',
        })
        .promise();

      const { data: msgData } = JSON.parse(
        data.Body?.toString('utf-8') || '{}'
      );

      if (msgData && Object.keys(msgData).length > 0) {
        // ── Step 2: Store in Redux ───────────────────────────────────
        dispatch(setConversationMessage(msgData));
        setConversationReplyMessage(msgData);
        setCount(1);
      }
    } catch (err: any) {
      console.error('S3 error:', err);
    }
  };

  useEffect(() => {
    dispatch(setConversationMessage(null));
    setConversationReplyMessage(null);
    fetchS3ConversationMessages();
  }, []);

  /**
   * @example handleClose()
   */
  const handleClose = () => {
    setAnchorEl(null);
    setShowPopOver(false);
  };
  return (
    <AppBar position="static" className={styles.header}>
      {showPopOver && (
        <CcfAcdHeaderPopover
          popoverFeature={popoverFeature}
          anchorEl={anchorEl}
          handleClose={handleClose}
          conversationReplyMessage={conversationReplyMessage}
          onNotificationRead={handleConversationNotificationRead}
        />
      )}
      <div className={styles.headerContainer}>
        <div className={styles.headerLogo}>
          <Toolbar className="toolbar" style={{ justifyContent: 'flex-start' }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="apps"
              className={styles.zeroBorderRadius}
            >
              <div className={styles.appsIconWithChannel}>
                <React.Fragment key={'left'}>
                  {/* <AppsIcon
                    id="appLauncher"
                    // onClick={toggleDrawer('left', true)}
                    className="appsIcon"
                  ></AppsIcon> */}
                  <CcfAgentDashboardIcon />
                </React.Fragment>
                <div className={styles.headerText}>Agent</div>
              </div>
            </IconButton>

            {/* {showHeaderText && headerText !== '' && (
              <CcfBox
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '10px',
                }}
              >
                <CcfHeaderIcon
                  style={{ fontSize: '35px', marginBottom: '3px' }}
                />
                <CcfTypography style={{ paddingLeft: '10px', color: 'white' }}>
                  {headerText}
                </CcfTypography>
              </CcfBox>
            )} */}
          </Toolbar>
        </div>
        <div>
          <CcfBox className="cxoneLogo">
            <CcfCxoneLogoIcon
              style={{
                width: '140px',
                height: '40px',
              }}
            />
          </CcfBox>
        </div>

        <Grid
          item
          lg={6}
          md={12}
          sm={12}
          xs={6}
          className={styles.headerFunctionsContainer}
        >
          <Toolbar className="toolbar">
            {isAuthorized && (
              <div className="adminblock">
                <AdminPanelSettingsIcon
                  style={{ cursor: 'pointer', marginRight: '10px' }}
                  onClick={() => window.open('/admin')}
                />
              </div>
            )}
            {isPartnerAccessTabVisible &&
              authorizePartnersAccess.find(
                (x: any) => assignName && x === assignName
              ) && (
                <>
                  <div
                    id="partnerAccessIcon"
                    className="simulatorBlock"
                    onClick={() => navigateToPartnerAccess()}
                  >
                    <AdminPanelSettingsIcon />
                  </div>
                </>
              )}
            {isSimulatorTabVisible &&
              currentUrlPathName !== '/simulator-v2' && (
                <CcfTooltip title={translate('simulator')} arrow>
                  <IconButton
                    id="simulatorIcon"
                    className="simulatorBlock"
                    aria-label="simulatorNavigation"
                    onClick={() => navigateToSimulator()}
                  >
                    <CcfNavigateToSimulatorIcon
                      style={{ fontSize: '1.7rem' }}
                    />
                  </IconButton>
                </CcfTooltip>
              )}
            {`${[
              lastContact[0]?.customer?.firstName,
              lastContact[0]?.customer?.middleName,
              lastContact[0]?.customer?.lastName,
            ]
              .filter(Boolean)
              .join(' ')}` &&
              lastContact[0]?.copilotSource === 'Live LLM' && (
                <>
                  <div
                    className="simulatorBlock"
                    onClick={() => {
                      const currentDate = new Date();
                      const dd = String(currentDate.getDate()).padStart(2, '0');
                      const mm = String(currentDate.getMonth() + 1).padStart(
                        2,
                        '0'
                      );
                      const hh = String(currentDate.getHours()).padStart(
                        2,
                        '0'
                      );
                      const MM = String(currentDate.getMinutes()).padStart(
                        2,
                        '0'
                      );
                      const ss = String(currentDate.getSeconds()).padStart(
                        2,
                        '0'
                      );
                      const generatedNumber = parseInt(dd + mm + hh + MM + ss);
                      const id = generatedNumber.toString();
                      if (
                        lastContact[0]?.customerChatReplyJsonData?.length > 0
                      ) {
                        dispatch(setJsonFileUploadStatus(true));
                      }
                      // eslint-disable-next-line react-hooks/rules-of-hooks
                      dispatch(
                        useSimulatorToInsertContact({
                          ...lastContact[0],
                          id: id,
                        })
                      );
                    }}
                  >
                    <CcfTooltip title={translate('refresh')} arrow>
                      <div>
                        <CcfReloadIcon htmlColor={'#fff'} viewBox="0 0 27 27" />
                      </div>
                    </CcfTooltip>
                  </div>
                </>
              )}
            <CcfTooltip title={translate('openHelpCenterInNewWindow')} arrow>
              <IconButton id="helpIcon" className={styles.toolboxActions}>
                <CcfHelpIcon htmlColor={'#fff'} />
              </IconButton>
            </CcfTooltip>
            <CcfTooltip title={translate('notifications')} arrow>
              <IconButton
                id="notificationsIcon"
                aria-label={'notifications'}
                className={styles.toolboxActions}
                data-testid="notifications"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  setShowPopOver(true);
                  setPopoverFeature('Notification');
                  setAnchorEl(e?.currentTarget);
                }}
              >
                <Badge
                  badgeContent={count}
                  classes={{
                    badge: 'notificationBadge',
                  }}
                >
                  <CcfNotificationIcon />
                </Badge>
              </IconButton>
            </CcfTooltip>
            <CcfAgentLeg />
            <div
              color="inherit"
              aria-label="agent state"
              className="userProfile"
            >
              <CcfAgentState agentStateForEmbeededView={false}></CcfAgentState>
            </div>
            {/* <Divider                  // not used by Eternals
              orientation={'vertical'}
              variant={'fullWidth'}
              className={classes.appHeaderDivider}
              sx={{ borderColor: '#fff !important' }}
            />
            <Select
              value={coPilotConfigurations?.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Select language', readOnly: true }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 48 * 10,
                    overflow: 'auto',
                  },
                },
              }}
              sx={{
                color: 'white',
                marginRight: '20px',
                '& .MuiInputBase-input': {
                  border: 'none',
                },
                '&:before, &:after': {
                  border: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover:not(.Mui-disabled):before': {
                  border: 'none',
                },
                '& .MuiSelect-icon': {
                  display: 'none',
                },
              }}
            >
              {supportedLanguages.map((language) => (
                <MenuItem key={language.code} value={language.code}>
                  {language.label}
                </MenuItem>
              ))}
            </Select> */}
          </Toolbar>
        </Grid>
      </div>
      <ToastContainer
        enableMultiContainer
        containerId={'AgentMessageToastContainer'}
        position="top-right"
        newestOnTop={false}
        closeButton={false}
        closeOnClick={false}
        rtl={false}
      />
    </AppBar>
  );
}

export default CcfAppHeader;
